import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export const ProductCard = ({ product: p }) => {
  const { add, setOpen } = useCart();
  const discount = p.compare_at_price && p.compare_at_price > p.price ? Math.round((1 - p.price / p.compare_at_price) * 100) : 0;
  const defaultVariant = p.variants?.find((v) => v.available) || p.variants?.[0];
  const soldOut = !p.available || !defaultVariant;

  const quickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (soldOut) return;
    add(
      {
        slug: p.slug,
        name: p.name,
        price: defaultVariant.price,
        image: p.image,
        variant_id: defaultVariant.id,
        variant_title: defaultVariant.title === "Default Title" ? "" : defaultVariant.title,
      },
      1
    );
    toast.success(`${p.name} added to your bag`, {
      action: { label: "View Bag", onClick: () => setOpen(true) },
    });
  };

  return (
    <Link to={`/product/${p.slug}`} data-testid={`product-card-${p.slug}`} className="group block">
      <div className="img-frame relative aspect-[4/5] overflow-hidden rounded-xl">
        {p.image ? (
          <img
            src={p.image}
            alt={p.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
          />
        ) : (
          <div className="grid h-full w-full place-items-center text-xs text-neutral-400">No image</div>
        )}
        <div className="absolute left-3 top-3 flex flex-col gap-1.5">
          {p.badge && (
            <span className="rounded-full bg-neutral-900 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
              {p.badge}
            </span>
          )}
          {discount > 0 && (
            <span className="rounded-full bg-red-50 px-2.5 py-1 text-[10px] font-semibold text-red-800">-{discount}%</span>
          )}
          {soldOut && (
            <span data-testid={`sold-out-${p.slug}`} className="rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
              Sold Out
            </span>
          )}
        </div>
        {!soldOut && (
          <button
            data-testid={`quick-add-${p.slug}`}
            onClick={quickAdd}
            className="absolute bottom-3 left-3 right-3 flex translate-y-16 items-center justify-center gap-2 rounded-full bg-white/95 py-2.5 text-xs font-semibold text-neutral-900 opacity-0 shadow-lg backdrop-blur transition-all duration-300 hover:bg-neutral-900 hover:text-white group-hover:translate-y-0 group-hover:opacity-100"
          >
            <Plus size={14} /> Quick Add
          </button>
        )}
      </div>
      <div className="pt-3.5">
        <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-neutral-400">{p.category_name}</p>
        <h3 className="mt-1 font-heading text-[15px] font-semibold leading-snug tracking-tight text-neutral-900 transition-colors group-hover:text-neutral-600">
          {p.name}
        </h3>
        <div className="mt-1.5 flex items-baseline">
          <span className={`text-[15px] font-bold ${soldOut ? "text-neutral-400" : "text-neutral-900"}`}>{inr(p.price)}</span>
          {p.compare_at_price && p.compare_at_price > p.price && (
            <span className="ml-2 text-[13px] text-neutral-400 line-through">{inr(p.compare_at_price)}</span>
          )}
        </div>
      </div>
    </Link>
  );
};
